import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Select from "react-select";
import dayjs from "dayjs";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import OrderDetailsModal from "../AdminBookings/OrderDetailsModal";
import { GET_BOOKINGS } from "../AdminBookings/graphql";
import { GET_EMPLOYEES } from "../AdminLeaves/leaveShared";
import { useAuthz } from "../../auth/useAuthz";
import ScorecardTab from "./ScorecardTab";
import ServiceTimeTab from "./ServiceTimeTab";
import ProductivityTab from "./ProductivityTab";
import RiskTab from "./RiskTab";
import OfficeSpeedTab from "./OfficeSpeedTab";

// Team Insights: scorecard, real service time, productivity, risk flags
// and office speed - one period and professional filter shared by all.

const TABS = [
  { key: "scorecard", label: "Scorecard", hint: "One score per professional, with last period's", Comp: ScorecardTab },
  { key: "service", label: "Service time", hint: "Real Start–Finish against booked minutes", Comp: ServiceTimeTab },
  { key: "productivity", label: "Productivity", hint: "Bookings, revenue and shift use per day", Comp: ProductivityTab },
  { key: "risk", label: "Risk", hint: "Bookings that look wrong", Comp: RiskTab },
  { key: "office", label: "Office speed", hint: "How fast the office handles things", Comp: OfficeSpeedTab, permission: "officeActivity.read", noPeople: true },
];

const PRESETS = [
  { key: "thisMonth", label: "This month" },
  { key: "lastMonth", label: "Last month" },
  { key: "last7", label: "7 days" },
  { key: "last30", label: "30 days" },
  { key: "last90", label: "90 days" },
  { key: "custom", label: "Custom" },
];

const selectStyles = {
  control: (b) => ({ ...b, minHeight: 38, borderColor: "#e5e7eb", boxShadow: "none" }),
  multiValue: (b) => ({ ...b, backgroundColor: "#FBB033" }),
};

const TeamInsights = () => {
  const { can } = useAuthz();
  const tabs = TABS.filter((t) => !t.permission || can(t.permission));
  const [tab, setTab] = useState(() => {
    try {
      const saved = localStorage.getItem("teamInsightsTab");
      return TABS.some((t) => t.key === saved) ? saved : "scorecard";
    } catch (e) {
      return "scorecard";
    }
  });
  const [preset, setPreset] = useState("last30");
  const [custom, setCustom] = useState({ start: dayjs().subtract(29, "day").format("YYYY-MM-DD"), end: dayjs().format("YYYY-MM-DD") });
  const [employeeIds, setEmployeeIds] = useState([]);
  const [fetchBooking] = useMutation(GET_BOOKINGS);
  const [viewingBooking, setViewingBooking] = useState(null);
  const [openError, setOpenError] = useState("");

  const pickTab = (k) => {
    setTab(k);
    try {
      localStorage.setItem("teamInsightsTab", k);
    } catch (e) {
      // Private window: the tab just isn't remembered.
    }
  };

  const range = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    const back = (n) => dayjs().subtract(n - 1, "day").format("YYYY-MM-DD");
    if (preset === "thisMonth") return { startDate: dayjs().startOf("month").format("YYYY-MM-DD"), endDate: today };
    if (preset === "lastMonth") {
      const m = dayjs().subtract(1, "month");
      return { startDate: m.startOf("month").format("YYYY-MM-DD"), endDate: m.endOf("month").format("YYYY-MM-DD") };
    }
    if (preset === "last7") return { startDate: back(7), endDate: today };
    if (preset === "last90") return { startDate: back(90), endDate: today };
    if (preset === "custom") return { startDate: custom.start, endDate: custom.end };
    return { startDate: back(30), endDate: today };
  }, [preset, custom]);
  const filter = useMemo(() => ({ ...range, employeeIds }), [range, employeeIds]);

  const { data: empData } = useQuery(GET_EMPLOYEES, { variables: { data: {} }, fetchPolicy: "cache-first" });
  const options = useMemo(
    () =>
      (empData?.getEmployeelist?.employee || [])
        .map((e) => ({ value: String(e.id), label: e.fullName || `#${e.id}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [empData]
  );

  const onPick = (id) => {
    setEmployeeIds([String(id)]);
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  };
  const onOpenBooking = async (r) => {
    setOpenError("");
    try {
      const res = await fetchBooking({ variables: { data: { id: r.orderId, bookingId: r.bookingId } } });
      const order = (res.data?.getBookings?.orders || []).find((o) => String(o.id) === String(r.orderId));
      if (!order) throw new Error(res.data?.getBookings?.message || "Booking not found");
      setViewingBooking(order);
    } catch (e) {
      setOpenError(`Could not open the booking: ${e.message}`);
    }
  };

  const active = tabs.find((t) => t.key === tab) || tabs[0];
  const Comp = active.Comp;

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="max-w-7xl space-y-5 pb-16">
          <div>
            <h1 className="text-2xl font-semibold text-white">Team insights</h1>
            <p className="text-sm text-gray-400 mt-0.5">How each professional is really doing, how long services take, who is productive, what looks wrong, and how fast the office responds. Bookings by booking date (IST).</p>
          </div>

          <div className="flex flex-wrap gap-1 bg-white/5 rounded-xl p-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => pickTab(t.key)}
                title={t.hint}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${active.key === t.key ? "bg-[#FBB033] text-gray-900" : "text-gray-300 hover:text-white hover:bg-white/10"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap rounded-lg bg-gray-100 p-0.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={`px-3 py-1.5 rounded-md text-sm ${preset === p.key ? "bg-white shadow-sm font-semibold text-gray-900" : "text-gray-600 hover:text-gray-900"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <input type="date" value={custom.start} max={custom.end} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, start: e.target.value }))} className="border border-gray-200 rounded-lg px-2 py-1.5" />
                to
                <input type="date" value={custom.end} min={custom.start} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, end: e.target.value }))} className="border border-gray-200 rounded-lg px-2 py-1.5" />
              </div>
            )}
            {!active.noPeople && (
              <div className="min-w-[220px] flex-1 max-w-sm">
                <Select
                  isMulti
                  placeholder="All professionals"
                  options={options}
                  value={options.filter((o) => employeeIds.includes(o.value))}
                  onChange={(sel) => setEmployeeIds(sel ? sel.map((x) => x.value) : [])}
                  styles={selectStyles}
                />
              </div>
            )}
            <span className="text-xs text-gray-400 ml-auto">
              {dayjs(range.startDate).format("D MMM YYYY")} – {dayjs(range.endDate).format("D MMM YYYY")}
            </span>
          </div>

          {openError && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{openError}</div>}

          <Comp filter={active.noPeople ? range : filter} onPick={onPick} onOpenBooking={onOpenBooking} />
        </div>
      </Layout>
      {viewingBooking && <OrderDetailsModal booking={viewingBooking} onClose={() => setViewingBooking(null)} />}
    </div>
  );
};

export default TeamInsights;
